import React, { useEffect } from "react";
import { Calendar, FileText, DollarSign, Clipboard, Video, BookOpen } from "lucide-react";
import AOS from "aos";
import { motion } from "framer-motion";
import Hero from "../components/Hero";

const StudentInfoDownloads = () => {
    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    const heroSlides = [
        {
            id: "student-info",
            image: "/assets/img/student-info.jpg",
            title: "Student Information & Downloads",
            subtitle: "Access all essential documents, forms, and resources in one place.",
            buttonText: "Explore Resources",
            buttonLink: "#resources-section",
        },
    ];

    const resources = [
        {
            id: 1,
            icon: Calendar,
            title: "Academic Calendar 2024",
            links: [
                {
                    label: "View",
                    url: "https://irp.cdn-website.com/1e753fb6/files/uploaded/2023.11.29 - 2024 ACADEMIC CALENDER.pdf",
                    isPrimary: true,
                },
            ],
        },
        {
            id: 2,
            icon: FileText,
            title: "Contracts",
            links: [
                {
                    label: "Online Class Timetable",
                    url: "https://irp.cdn-website.com/1e753fb6/files/uploaded/Jan%20-%20Jun%20Postgraduate%202023%20Online%20Lecture%20Timetable.pdf",
                },
                {
                    label: "Exam",
                    url: "https://irp.cdn-website.com/1e753fb6/files/uploaded/2023.08.01- MASTERS SEPTEMBER 2023 EXAM REGISTRATION FORM.docx",
                },
                {
                    label: "Graduation Fees",
                    url: "https://irp.cdn-website.com/1e753fb6/files/uploaded/2023.07.27 - ARU NOV 2023 GRADUATION REGISTRATION FORM.docx",
                },
            ],
        },
        {
            id: 3,
            icon: Video,
            title: "Online Classes Recordings Link",
            links: [
                {
                    label: "View",
                    url: "https://kuoa.sharepoint.com/:f:/s/ResidentialClassLive-Postgraduate/ErjqCNzhIK5Jm8B4IqHyP9kBEUigNnlFC0EMQPz0zeGuTw?e=olWKj5",
                    isPrimary: true,
                },
            ],
        },
        {
            id: 4,
            icon: DollarSign,
            title: "Fee Structure",
            links: [
                {
                    label: "View",
                    url: "https://irp.cdn-website.com/1e753fb6/files/uploaded/ARU_Fees_Schedule_and_Authorised_Bank_Account_2025_04_30.pdf",
                    isPrimary: true,
                },
                {
                    label: "Authorised Bank Account Details",
                    url: "https://irp.cdn-website.com/1e753fb6/files/uploaded/Authorised_Bank_Account_Details_for_Africa_Research_University_2025_04_30.pdf",
                },
            ],
        },
    ];

    const additionalResources = [
        {
            id: 5,
            icon: Clipboard,
            title: "Assignment Cover",
            links: [
                {
                    label: "View",
                    url: "https://irp.cdn-website.com/1e753fb6/files/uploaded/2021.07.15%20-%20Assignment%20cover.pdf",
                    isPrimary: true,
                },
            ],
        },
        {
            id: 6,
            icon: FileText,
            title: "Student Portal \"How-To\" Guide",
            links: [],
        },
        {
            id: 7,
            icon: BookOpen,
            title: "Protocols",
            links: [
                {
                    label: "View",
                    url: "tel:+260965 43 21 11",
                    isPrimary: true,
                },
            ],
        },
        {
            id: 8,
            icon: FileText,
            title: "Degree Programs",
            links: [],
        },
    ];

    const admissionsPolicy = {
        icon: Clipboard,
        title: "Admissions Policy",
        url: "https://irp.cdn-website.com/1e753fb6/files/uploaded/20211102%20Admissions%20Policy.pdf",
    };

    return (
        <section className="bg-gray-50 min-h-screen pt-1">

            <div className="container my-16 mx-auto px-6 py-16" id="resources-section">
                {/* Main Resources Grid */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-16"
                >
                    <h2 className="text-3xl font-bold text-center text-gray-800 mb-10">
                        Essential Resources
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {resources.map((resource, index) => (
                            <motion.div
                                key={resource.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                                className="bg-white rounded-lg border hover:shadow-md transition-all p-6 flex flex-col items-center text-center"
                            >
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                    <resource.icon className="w-8 h-8 text-green-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 min-h-[3rem] flex items-center">
                                    {resource.title}
                                </h3>
                                <div className="space-y-2 w-full">
                                    {resource.links.map((link, linkIndex) => (
                                        <a
                                            key={linkIndex}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`block w-full px-4 py-2 rounded-lg font-medium transition-all ${link.isPrimary
                                                ? "bg-green-600 text-white hover:bg-green-700"
                                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                }`}
                                        >
                                            {link.label}
                                        </a>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Additional Resources Grid */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="mb-16"
                >
                    <h2 className="text-3xl font-bold text-center text-gray-800 mb-10">
                        Additional Resources
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {additionalResources.map((resource, index) => (
                            <motion.div
                                key={resource.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 + index * 0.1 }}
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                                className="bg-white rounded-lg border hover:shadow-md transition-all p-6 flex flex-col items-center text-center"
                            >
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                    <resource.icon className="w-8 h-8 text-green-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4 min-h-[3rem] flex items-center">
                                    {resource.title}
                                </h3>
                                <div className="space-y-2 w-full">
                                    {resource.links.length > 0 ? (
                                        resource.links.map((link, linkIndex) => (
                                            <a
                                                key={linkIndex}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`block w-full px-4 py-2 rounded-lg font-medium transition-all ${link.isPrimary
                                                    ? "bg-green-600 text-white hover:bg-green-700"
                                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                    }`}
                                            >
                                                {link.label}
                                            </a>
                                        ))
                                    ) : (
                                        <span className="text-gray-500 text-sm">Coming Soon</span>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Admissions Policy - Full Width */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    data-aos="fade-up"
                    className="max-w-2xl mx-auto"
                >
                    <div className="bg-white rounded-lg border hover:shadow-md transition-all p-8 flex flex-col items-center text-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <admissionsPolicy.icon className="w-10 h-10 text-green-600" />
                        </div>
                        <h3 className="text-2xl font-semibold text-gray-800 mb-6">
                            {admissionsPolicy.title}
                        </h3>
                        <a
                            href={admissionsPolicy.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all"
                        >
                            View
                        </a>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default StudentInfoDownloads;